import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { numberSetService, type NumberSetItem } from '../../services/numberSetService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayForms.css'

/** รายการชนิด (ตรง user-classic NumberSetCreatePage) */
const PLAY_TYPE_OPTIONS = [
  { value: '', label: '-- เลือกชนิด --' },
  { value: '2_DIGIT_TOP', label: '2 ตัวบน' },
  { value: '2_DIGIT_BOTTOM', label: '2 ตัวล่าง' },
  { value: '2_DIGIT_TOD', label: '2 ตัวโต๊ด' },
  { value: '2_DIGIT_REVERSE', label: '2 ตัวกลับ' },
  { value: '3_DIGIT_TOP', label: '3 ตัวบน' },
  { value: '3_DIGIT_BOTTOM', label: '3 ตัวล่าง' },
  { value: '3_DIGIT_TOD', label: '3 ตัวโต๊ด' },
  { value: '3_DIGIT_REVERSE', label: '3 ตัวบน (กลับเลข)' },
  { value: 'RUNNING_TOP', label: 'วิ่งบน' },
  { value: 'RUNNING_BOTTOM', label: 'วิ่งล่าง' },
  { value: '4_DIGIT_TOP', label: '4 ตัวบน' },
  { value: '4_DIGIT_TOD', label: '4 ตัวโต๊ด' },
  { value: '5_DIGIT_TOD', label: '5 ตัวโต๊ด' },
  { value: 'FIXED_POSITION_ONES', label: 'ปักหลักหน่วย' },
  { value: 'FIXED_POSITION_TENS', label: 'ปักหลักสิบ' },
  { value: 'FIXED_POSITION_HUNDREDS', label: 'ปักหลักร้อย' },
]

function getExpectedDigitCount(prizeTypeCode: string): number {
  if (prizeTypeCode.startsWith('2_DIGIT')) return 2
  if (prizeTypeCode.startsWith('3_DIGIT')) return 3
  if (prizeTypeCode.startsWith('4_DIGIT')) return 4
  if (prizeTypeCode === '5_DIGIT_TOD') return 5
  if (prizeTypeCode.startsWith('RUNNING_') || prizeTypeCode.startsWith('FIXED_POSITION_')) return 1
  return 0
}

function validateNumbersForType(
  prizeTypeCode: string,
  numbersStr: string
): { valid: string[]; invalid: string[]; expectedLen: number } {
  const expectedLen = getExpectedDigitCount(prizeTypeCode)
  const rawList = numbersStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  const valid: string[] = []
  const invalid: string[] = []
  rawList.forEach((num) => {
    if (num.length === expectedLen) valid.push(num)
    else invalid.push(num)
  })
  return { valid, invalid, expectedLen }
}

const sanitizeNumberInput = (raw: string) => raw.replace(/[^\d,\s]/g, '')

/** แถวละหนึ่งเลข — แยกช่อง พร้อมแทงได้ (ตรงรูปที่ 1) */
interface NumberSetRow {
  id: number
  prizeTypeCode: string
  typeLabel: string
  number: string
}

/** หน้า แก้ไขเลขชุด / สร้างเลขชุด — โครงแบบเดียวกับ huaycreatenumberset.html (สร้าง = ข้อมูลเปล่า) */
const ModernLobbyHuaySetEditPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [numberSetName, setNumberSetName] = useState('')
  /** แถวละหนึ่งเลข — แยกช่องตามรูปที่ 1 */
  const [rows, setRows] = useState<NumberSetRow[]>([])
  const [selectedType, setSelectedType] = useState('')
  const [numberInput, setNumberInput] = useState('')

  const loadOne = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await numberSetService.get(id)
      if (data) {
        setNumberSetName(data.name)
        const nextRows: NumberSetRow[] = []
        let rowId = Date.now()
        ;(data.items || []).forEach((item) => {
          const nums = sanitizeNumberInput(String(item.numbers || '')).split(/[\s,]+/).filter(Boolean)
          nums.forEach((num) => {
            nextRows.push({
              id: rowId++,
              prizeTypeCode: item.prizeTypeCode,
              typeLabel: item.typeLabel,
              number: num,
            })
          })
        })
        setRows(nextRows)
      } else {
        setError('ไม่พบเลขชุดนี้')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (isEdit) loadOne()
  }, [isEdit, loadOne])

  const toastWarn = (msg: string) => {
    Swal.fire({
      toast: true,
      position: 'top',
      icon: 'warning',
      title: msg,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })
  }
  const toastSuccess = (msg: string) => {
    Swal.fire({
      toast: true,
      position: 'top',
      icon: 'success',
      title: msg,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    })
  }

  const handleRemoveItem = useCallback((rowId: number) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId))
  }, [])

  /** เมื่อพิมพ์เลข: ครบหลักตามชนิด (จาก select) ให้ใส่ , อัตโนมัติ; ถ้าพิมพ์ , เองก่อนครบหลักให้ตัดออก */
  const handleNumberInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const sanitized = sanitizeNumberInput(raw)
      const expectedLen = selectedType ? getExpectedDigitCount(selectedType) : 0

      if (expectedLen > 0) {
        if (sanitized.endsWith(',')) {
          const segmentBeforeComma = (sanitized.slice(0, -1).split(/[\s,]+/).pop() || '').trim()
          if (segmentBeforeComma.length !== expectedLen) {
            toastWarn(`กรุณาพิมพ์ให้ครบ ${expectedLen} หลักก่อนค่อยใส่ , (ตอนนี้ ${segmentBeforeComma.length} หลัก)`)
            setNumberInput(sanitized.slice(0, -1))
            return
          }
        } else {
          const parts = sanitized.split(/[\s,]+/).filter(Boolean)
          const lastPart = (parts[parts.length - 1] || '').trim()
          if (lastPart.length === expectedLen) {
            setNumberInput(sanitized.trimEnd() + ',')
            return
          }
        }
      }
      setNumberInput(sanitized)
    },
    [selectedType]
  )

  const handleAddNumberSet = useCallback(() => {
    const cleaned = sanitizeNumberInput(numberInput).trim()
    if (!selectedType || !cleaned) {
      toastWarn(selectedType ? 'กรุณากรอกเลข' : 'กรุณาเลือกชนิด')
      return
    }
    const opt = PLAY_TYPE_OPTIONS.find((o) => o.value === selectedType)
    const typeLabel = opt?.label ?? selectedType
    const { valid, invalid, expectedLen } = validateNumbersForType(selectedType, cleaned)
    if (invalid.length > 0) {
      toastWarn(`ชนิด "${typeLabel}" ต้องเป็นเลข ${expectedLen} หลัก — เลขที่ผิด: ${invalid.join(', ')}`)
      return
    }
    const baseId = Date.now()
    setRows((prev) => [
      ...prev,
      ...valid.map((num, i) => ({
        id: baseId + i,
        prizeTypeCode: selectedType,
        typeLabel,
        number: num,
      })),
    ])
    setSelectedType('')
    setNumberInput('')
  }, [selectedType, numberInput])

  /** รวมแถว (แถวละหนึ่งเลข) กลับเป็นรูปแบบ API (กลุ่มตามชนิด, numbers คั่นด้วย comma) */
  const rowsToApiItems = useCallback((r: NumberSetRow[]): NumberSetItem[] => {
    const byKey = new Map<string, NumberSetRow[]>()
    r.forEach((row) => {
      const key = row.prizeTypeCode
      if (!byKey.has(key)) byKey.set(key, [])
      byKey.get(key)!.push(row)
    })
    return Array.from(byKey.entries()).map(([_, group]) => ({
      prizeTypeCode: group[0].prizeTypeCode,
      typeLabel: group[0].typeLabel,
      numbers: group.map((x) => x.number).join(', '),
    }))
  }, [])

  const handleSave = useCallback(async () => {
    const name = numberSetName.trim()
    if (!name) {
      toastWarn('กรุณาระบุชื่อเลขชุด')
      return
    }
    if (rows.length === 0) {
      toastWarn('กรุณาเพิ่มรายการในตารางอย่างน้อย 1 รายการ')
      return
    }
    const expectedLenByRow = rows.map((r) => getExpectedDigitCount(r.prizeTypeCode))
    const invalidRow = rows.find((r, i) => r.number.length !== expectedLenByRow[i])
    if (invalidRow) {
      toastWarn(`"${invalidRow.typeLabel}" ต้องเป็นเลข ${getExpectedDigitCount(invalidRow.prizeTypeCode)} หลัก — เลขที่ผิด: ${invalidRow.number}`)
      return
    }
    try {
      setSaving(true)
      if (isEdit && id) {
        await numberSetService.update(id, { name, items: rowsToApiItems(rows) })
        toastSuccess('บันทึกเลขชุดเรียบร้อยแล้ว')
      } else {
        await numberSetService.create({ name, items: rowsToApiItems(rows) })
        toastSuccess('สร้างเลขชุดเรียบร้อยแล้ว')
        navigate('/lobby-huay/set')
      }
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [id, isEdit, numberSetName, rows, rowsToApiItems, navigate])

  const handleClearAll = useCallback(() => {
    setNumberSetName('')
    setRows([])
    setSelectedType('')
    setNumberInput('')
  }, [])

  const handleDeleteSet = useCallback(async () => {
    if (!id) return
    const confirmed = await Swal.fire({
      title: 'ลบเลขชุดทิ้ง?',
      text: 'การดำเนินการนี้ไม่สามารถย้อนกลับได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
    })
    if (!confirmed.isConfirmed) return
    try {
      setSaving(true)
      await numberSetService.delete(id)
      toastSuccess('ลบเลขชุดเรียบร้อยแล้ว')
      navigate('/lobby-huay/set')
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'ลบไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }, [id, navigate])

  if (isEdit && loading) {
    return (
      <div className="htd-form">
        <div className="lobby-huay-page-header">
          <Link to="/lobby-huay/set" className="lobby-huay-page-header__back">
            <i className="fas fa-chevron-left" aria-hidden /> กลับ
          </Link>
          <h3 className="lobby-huay-page-header__title">แก้ไขเลขชุด</h3>
        </div>
        <div className="htd-slip__state" aria-busy="true">
          <i className="fas fa-spinner fa-spin" style={{ color: '#7dd3fc', fontSize: '1.4rem' }} />
          <p>กำลังโหลด...</p>
        </div>
      </div>
    )
  }

  if (isEdit && error) {
    return (
      <div className="htd-form">
        <div className="lobby-huay-page-header">
          <Link to="/lobby-huay/set" className="lobby-huay-page-header__back">
            <i className="fas fa-chevron-left" aria-hidden /> กลับ
          </Link>
          <h3 className="lobby-huay-page-header__title">แก้ไขเลขชุด</h3>
        </div>
        <div className="htd-form__alert" role="alert">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="htd-form">
      <div className="lobby-huay-page-header">
        <Link to="/lobby-huay/set" className="lobby-huay-page-header__back">
          <i className="fas fa-chevron-left" aria-hidden /> กลับ
        </Link>
        <h3 className="lobby-huay-page-header__title">{isEdit ? 'แก้ไขเลขชุด' : 'สร้างเลขชุด'}</h3>
      </div>

      <div className="htd-form__panel">
        <label className="htd-form__label" htmlFor="htd-set-name">
          ระบุชื่อเรียกเลขชุด
        </label>
        <div className="htd-form__field">
          <i className="fas fa-keyboard" aria-hidden />
          <input
            id="htd-set-name"
            type="text"
            placeholder="ชื่อเลขชุด"
            value={numberSetName}
            onChange={(e) => setNumberSetName(e.target.value)}
          />
        </div>
      </div>

      <div className="htd-form__table-wrap">
        <table className="htd-form__table">
          <thead>
            <tr>
              <th>##</th>
              <th>ชนิด</th>
              <th>เลข</th>
              <th>ลบ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <p className="htd-form__empty">ยังไม่มีรายการ — เลือกชนิดแล้วกรอกเลขด้านล่าง</p>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.id}>
                  <td>{idx + 1}</td>
                  <td>
                    <span className="htd-form__type-chip">{row.typeLabel}</span>
                  </td>
                  <td>
                    <strong>{row.number}</strong>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="htd-form__icon-btn"
                      aria-label={`ลบเลข ${row.number}`}
                      onClick={() => handleRemoveItem(row.id)}
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="htd-form__panel">
        <p className="htd-form__label">กรอกเลข</p>
        <div className="htd-form__actions" style={{ marginBottom: '0.55rem' }}>
          <div className="htd-form__field">
            <i className="fas fa-list" aria-hidden />
            <select
              name="playtype"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              aria-label="เลือกชนิด"
            >
              {PLAY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'empty'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="htd-form__field">
            <i className="fas fa-hashtag" aria-hidden />
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={
                selectedType
                  ? `เลข ${getExpectedDigitCount(selectedType)} หลัก คั่นด้วย ,`
                  : 'เลข เช่น 11,22,33,44,55'
              }
              value={numberInput}
              onChange={handleNumberInputChange}
            />
          </div>
        </div>
        <button type="button" className="htd-form__btn htd-form__btn--primary" style={{ width: '100%' }} onClick={handleAddNumberSet}>
          <i className="fas fa-plus-circle" aria-hidden />
          เพิ่มชุดตัวเลข
        </button>
      </div>

      <div className="htd-form__actions">
        <button
          type="button"
          className="htd-form__btn htd-form__btn--primary"
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? <i className="fas fa-spinner fa-spin" aria-hidden /> : <i className="fas fa-check-circle" aria-hidden />}
          {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
        </button>
        {isEdit ? (
          <button
            type="button"
            className="htd-form__btn htd-form__btn--danger"
            disabled={saving}
            onClick={() => void handleDeleteSet()}
          >
            <i className="fas fa-times-circle" aria-hidden />
            ลบเลขชุดทิ้ง
          </button>
        ) : (
          <button
            type="button"
            className="htd-form__btn htd-form__btn--secondary"
            disabled={saving}
            onClick={handleClearAll}
          >
            <i className="fas fa-eraser" aria-hidden />
            ล้างทั้งหมด
          </button>
        )}
      </div>
    </div>
  )
}

export default ModernLobbyHuaySetEditPage
